import { useCallback } from 'react';

import useStores from 'shared/hooks/use-stores';

import { ModalNames, useModals } from 'components/modals';
import { editBankAccount } from 'src/payments/constants';
import useDutchiePayEnrollment from 'src/hooks/use-dutchiePay-enrollment';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { BankDetailResponse, getBankDetails } from 'src/payments/utils/bank-utils';
import { useCartPayment } from 'src/checkout/components/sections/payment/shared/use-cart-payment';
import { useDutchiePay } from 'src/payments/hooks';

export type UsePayByBankDetailsResponse = {
  bankDetails: BankDetailResponse | null;
  bankNeedsRelinking: boolean;
  onEditBank: () => void;
  onRelinkBank: () => void;
  showBankInformation: boolean;
  loggedOutPBBSignupEnabled: boolean;
  loggedInPBBSignupEnabled: boolean;
  hasOtherOrderTypesWithPBBEnabled: boolean;
  openTermsModal: () => Promise<unknown>;
};

export const usePayByBankDetails = (): UsePayByBankDetailsResponse => {
  // Hooks
  const { openModal } = useModals();
  const { Cart, UI } = useStores();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const {
    bankInformation,
    showBankInformation,
    hasOtherOrderTypesWithPBBEnabled,
    loggedInPBBSignupEnabled,
    loggedOutPBBSignupEnabled,
  } = useCartPayment();

  const { needsRelinkBankAccount: bankNeedsRelinking } = useDutchiePay();

  const { openDutchiePay } = useOpenDutchiePay({
    cname: Cart.dispensary.cName,
    onCloseModal: () => DutchiePayEnrollment.setAutoCheckoutAfterBankLinkReconnect(false),
  });

  const bankDetails = bankInformation ? getBankDetails(bankInformation) : null;
  // Handlers
  const openTermsModal = useCallback(() => openModal(ModalNames.termsOfService), [openModal]);

  const onEditBank = (): void => {
    if (UI?.isMobileEcommApp) {
      void openModal(ModalNames.dutchiePayVisitOnDesktop);
    } else {
      DutchiePayEnrollment.editBankAccount();
      openDutchiePay(editBankAccount);
    }
  };

  const onRelinkBank = (): void => {
    void openModal(ModalNames.dutchiePayRelinkBankAccount);
  };
  return {
    bankDetails,
    bankNeedsRelinking,
    showBankInformation,
    loggedOutPBBSignupEnabled,
    loggedInPBBSignupEnabled,
    hasOtherOrderTypesWithPBBEnabled,
    onEditBank,
    onRelinkBank,
    openTermsModal,
  };
};
